import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import TextField from "@components/ui/textfields/TextField";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import DropDown from "@components/ui/dropdown/DropDown";
import DropDownMultiple from "@components/ui/dropdown/DropDownMultiple";
import DateTimePicker from "@components/ui/selection/DateTimePicker";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import { useState, useRef, useEffect, useMemo } from "react";
import Avvvatars from "avvvatars-react";
import ChatPanel from "@components/chat/ChatPanel";
import { ordresApi } from "@services/api/ordres";
import { dossiersApi } from "@services/api/dossiers";
import { useOrdreStore } from "@store/ordreStore";
import { usePlanningStore } from "@store/planningStore";
import { useAtelierStore } from "@store/atelierStore";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";
import type {
  Ordre,
  OrdreStatus,
  OrdreTag,
  OrdreIntervention,
  OrdreDocument,
} from "types/ordre";
import type { Contact } from "types/contact";
import type { Dossier } from "types/dossier";
import type { ClientVehicle } from "types/client_vehicle";
import { interventionsApi, type Intervention } from "@services/api/interventions";

// ─── Constants ─────────────────────────────────────────────

const TABS = [
  { value: "ordre", label: "Ordre" },
  { value: "interventions", label: "Interventions" },
  { value: "atelier", label: "Atelier" },
  { value: "contacts", label: "Contacts" },
  { value: "vehicules", label: "Véhicules" },
  { value: "documents", label: "Documents" },
  { value: "communications", label: "Chat" },
];

const STATUS_OPTIONS: { value: OrdreStatus; label: string }[] = [
  { value: "a_preparer", label: "À préparer" },
  { value: "commande", label: "Commandé" },
  { value: "pret", label: "Prêt" },
  { value: "reception", label: "Réception" },
  { value: "en_cours", label: "En cours" },
  { value: "devis", label: "Devis" },
  { value: "accord", label: "Accord" },
  { value: "fin_de_travaux", label: "Fin de travaux" },
  { value: "facturation", label: "Facturation" },
  { value: "disponible", label: "Disponible" },
  { value: "restitue", label: "Restitué" },
];

const TAG_OPTIONS: { value: OrdreTag; label: string }[] = [
  { value: "huile", label: "Huile" },
  { value: "diag", label: "Diagnostic" },
  { value: "pieces_en_attente", label: "Pièces en attente" },
  { value: "accidente", label: "Accidenté" },
  { value: "assurance", label: "Assurance" },
];

const COLORIS_TO_PAINT: Record<string, string> = {
  noir: "black",
  blanc: "white",
  gris: "grey",
  bleu: "blue",
  rouge: "red",
  vert: "green",
  jaune: "yellow",
  marron: "brown",
  orange: "orange",
  autre: "silver",
};

function getVehicleImageUrl(marque?: string, modele?: string, coloris?: string): string | null {
  if (!marque || !modele) return null;
  const make = encodeURIComponent(marque.toLowerCase());
  const modelFamily = encodeURIComponent(modele.toLowerCase());
  const paint = COLORIS_TO_PAINT[coloris || ""] || "grey";
  return `https://cdn.imagin.studio/getimage?customer=img&make=${make}&modelFamily=${modelFamily}&modelYear=2023&zoomType=fullscreen&angle=0&paintDescription=${paint}`;
}

function formatHour(h: number): string {
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return `${hrs}h${mins.toString().padStart(2, "0")}`;
}

function formatTimestamp(ts?: { seconds: number }): string {
  if (!ts) return "—";
  return new Date(ts.seconds * 1000).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// ─── Sub-modal for adding items ────────────────────────────

function AddSubModal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <BaseModal
      title={title}
      startIcon={<Icon1 name="add" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-3 p-4">{children}</div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button variant="small" label="Ajouter" onClick={onClose} />
      </div>
    </BaseModal>
  );
}

// ─── Tab content components ────────────────────────────────

function OrdreTab({ form, update }: { form: Ordre; update: <K extends keyof Ordre>(key: K, value: Ordre[K]) => void }) {
  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <TextField label="N° Ordre" value={form.num} onChange={(v) => update("num", v)} />
        </div>
        <div className="flex-1">
          <TextField label="Nom" value={form.name} onChange={(v) => update("name", v)} />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <DropDown
            label="Statut"
            options={STATUS_OPTIONS}
            value={form.status}
            onSelect={(v) => update("status", v as OrdreStatus)}
          />
        </div>
        <div className="flex-1">
          <TextField label="Assigné à" value={form.assigned_to || ""} onChange={(v) => update("assigned_to", v)} />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <TextField label="Immatriculation" value={form.immatriculation || ""} onChange={(v) => update("immatriculation", v)} />
        </div>
        <div className="flex-1">
          <TextField label="Modèle" value={form.modele || ""} onChange={(v) => update("modele", v)} />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <DateTimePicker
            label="Date réception"
            seconds={form.reception_date?.seconds || null}
            onChange={(s) => update("reception_date", { seconds: s })}
          />
        </div>
        <div className="flex-1">
          <DateTimePicker
            label="Date restitution"
            seconds={form.restitution?.seconds || null}
            onChange={(s) => update("restitution", { seconds: s })}
          />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <TextField label="Montant TTC (€)" type="number" value={form.amount != null ? String(form.amount) : ""} onChange={(v) => update("amount", v ? parseFloat(v) : undefined)} />
        </div>
        <div className="flex-1">
          <TextField label="Campagne" value={form.campagne || ""} onChange={(v) => update("campagne", v)} />
        </div>
      </div>
      <DropDownMultiple
        label="Tags"
        options={TAG_OPTIONS}
        value={form.tags}
        onSelect={(items) => update("tags", items as OrdreTag[])}
      />
      <AreaTextField
        label="Notes"
        value={form.notes || ""}
        onChange={(v) => update("notes", v)}
        rows={3}
      />
    </div>
  );
}

// ─── Add Intervention Sub-Modal ────────────────────────────

function AddInterventionModal({ onClose }: { onClose: () => void }) {
  const [label, setLabel] = useState("");
  const [defaultH, setDefaultH] = useState("");
  const [estimatedH, setEstimatedH] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  return (
    <AddSubModal title="Ajouter une intervention" onClose={onClose}>
      <TextField label="Libellé" value={label} onChange={setLabel} />
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <TextField label="Durée défaut (h)" type="number" value={defaultH} onChange={setDefaultH} />
        </div>
        <div className="flex-1">
          <TextField label="Durée estimée (h)" type="number" value={estimatedH} onChange={setEstimatedH} />
        </div>
      </div>
      <TextField label="Assigné à" value={assignedTo} onChange={setAssignedTo} />
    </AddSubModal>
  );
}

function AddContactModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  return (
    <AddSubModal title="Ajouter un contact" onClose={onClose}>
      <TextField label="Nom" value={name} onChange={setName} />
      <TextField label="Rôle" value={role} onChange={setRole} />
      <TextField label="Téléphone" value={phone} onChange={setPhone} />
      <TextField label="Email" value={email} onChange={setEmail} />
    </AddSubModal>
  );
}

function AddVehicleModal({ onClose }: { onClose: () => void }) {
  const [plate, setPlate] = useState("");
  const [marque, setMarque] = useState("");
  const [modele, setModele] = useState("");
  const [vin, setVin] = useState("");
  return (
    <AddSubModal title="Ajouter un véhicule" onClose={onClose}>
      <TextField label="Immatriculation" value={plate} onChange={setPlate} />
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <TextField label="Marque" value={marque} onChange={setMarque} />
        </div>
        <div className="flex-1">
          <TextField label="Modèle" value={modele} onChange={setModele} />
        </div>
      </div>
      <TextField label="VIN" value={vin} onChange={setVin} />
    </AddSubModal>
  );
}

// ─── Tab content components (Interventions, Atelier, Contacts, Vehicules, Documents) ──

interface AtelierChargeDisplay {
  id: string;
  worker_name: string;
  specialty: string;
  date: string;
  start_hour: number;
  duration_hours: number;
}

function InterventionsTab({ interventions, onAdd, configuredInterventions, onAddFromCatalog }: {
  interventions: Ordre["interventions"];
  onAdd: () => void;
  configuredInterventions: Intervention[];
  onAddFromCatalog: (catalog: Intervention) => void;
}) {
  const [showPicker, setShowPicker] = useState(false);
  const CATEGORY_LABELS: Record<string, string> = {
    mecanique: "Mécanique", carrosserie: "Carrosserie", vitrage: "Vitrage", pneumatique: "Pneumatique",
  };
  return (
    <div className="flex flex-col p-4 gap-2">
      <div className="flex flex-row items-center justify-between">
        <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#525252" }}>Interventions</span>
        <div className="relative">
          <button
            onClick={() => setShowPicker(!showPicker)}
            className="flex flex-row items-center gap-1 text-h4 font-medium hover:bg-neutral-100 rounded-lg px-2 py-1"
            style={{ color: "#6366f1" }}
          >
            <Icon1 name="plus" size={12} color="#6366f1" />
            Ajouter
          </button>
          {showPicker && (
            <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg border border-neutral-200 shadow-lg overflow-y-auto" style={{ maxHeight: 280, width: 320 }}>
              {Object.entries(CATEGORY_LABELS).map(([cat, label]) => {
                const items = configuredInterventions.filter((i) => i.category === cat && i.active);
                if (items.length === 0) return null;
                return (
                  <div key={cat}>
                    <div className="px-3 py-1 text-h4 font-semibold text-neutral-400 bg-neutral-50 sticky top-0">{label}</div>
                    {items.map((item) => {
                      const h = Math.floor(item.estimated_minutes / 60);
                      const m = item.estimated_minutes % 60;
                      const dur = h > 0 && m > 0 ? `${h}h${m.toString().padStart(2, "0")}` : h > 0 ? `${h}h` : `${m}min`;
                      return (
                        <button key={item.id} className="flex flex-row items-center justify-between w-full px-3 py-2 hover:bg-indigo-50 text-left" onClick={() => { onAddFromCatalog(item); setShowPicker(false); }}>
                          <span className="text-h3 text-neutral-600">{item.name}</span>
                          <span className="text-h4 text-neutral-400 ml-2">{dur}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              <div className="border-t border-neutral-100">
                <button className="flex flex-row items-center gap-2 w-full px-3 py-2 hover:bg-neutral-50 text-left" onClick={() => { onAdd(); setShowPicker(false); }}>
                  <Icon1 name="plus" size={12} color="#A1A1A1" />
                  <span className="text-h3 text-neutral-400">Intervention personnalisée</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {interventions.map((inter, i) => (
        <div key={i} className="flex flex-row items-center justify-between bg-white rounded-lg border border-neutral-200 px-3 py-2">
          <div className="flex flex-col">
            <span className="text-h3 font-medium" style={{ fontFamily: "Poppins" }}>{inter.label}</span>
            <span className="text-h4 text-neutral-400" style={{ fontFamily: "Poppins" }}>
              {inter.estimated_hours || inter.default_hours}h — {inter.assigned_to || "Non assigné"}
            </span>
          </div>
        </div>
      ))}
      {interventions.length === 0 && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucune intervention</span>
      )}
    </div>
  );
}

function AtelierTab({ charges }: { charges: AtelierChargeDisplay[] }) {
  return (
    <div className="flex flex-col p-4 gap-2">
      <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#525252" }}>Charges atelier</span>
      {charges.map((c) => (
        <div key={c.id} className="flex flex-row items-center justify-between bg-white rounded-lg border border-neutral-200 px-3 py-2">
          <div className="flex flex-col">
            <span className="text-h3 font-medium" style={{ fontFamily: "Poppins" }}>{c.worker_name}</span>
            <span className="text-h4 text-neutral-400" style={{ fontFamily: "Poppins" }}>
              {c.specialty} — {c.date} — {c.duration_hours}h
            </span>
          </div>
        </div>
      ))}
      {charges.length === 0 && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucune charge assignée</span>
      )}
    </div>
  );
}

function ContactsTab({ contacts, onAdd }: { contacts: Ordre["contacts"]; onAdd: () => void }) {
  return (
    <div className="flex flex-col p-4 gap-2">
      <div className="flex flex-row items-center justify-between">
        <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#525252" }}>Contacts</span>
        <button
          onClick={onAdd}
          className="flex flex-row items-center gap-1 text-h4 font-medium hover:bg-neutral-100 rounded-lg px-2 py-1"
          style={{ color: "#6366f1" }}
        >
          <Icon1 name="plus" size={12} color="#6366f1" />
          Ajouter
        </button>
      </div>
      {(contacts || []).map((c, i) => {
        const displayName = [c.prenom, c.nom].filter(Boolean).join(" ") || "—";
        return (
          <div key={i} className="flex flex-row items-center gap-3 bg-white rounded-lg border border-neutral-200 px-3 py-2">
            <div className="flex-shrink-0 overflow-hidden" style={{ borderRadius: 999 }}>
              <Avvvatars value={displayName} style="shape" size={32} />
            </div>
            <div className="flex flex-col">
              <span className="text-h3 font-medium" style={{ fontFamily: "Poppins" }}>{displayName}</span>
              <span className="text-h4 text-neutral-400" style={{ fontFamily: "Poppins" }}>{c.role || "—"} · {c.phone || c.email || "—"}</span>
            </div>
          </div>
        );
      })}
      {(!contacts || contacts.length === 0) && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucun contact</span>
      )}
    </div>
  );
}

function VehiculesTab({
  dossierVehicles,
  selectedVehicleId,
  onSelect,
}: {
  dossierVehicles: Array<{ id: string; plate?: string; marque?: string; modele?: string; coloris?: string; vin?: string; km?: number }>;
  selectedVehicleId?: string;
  onSelect: (vehicleId: string) => void;
}) {
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  return (
    <div className="flex flex-col p-4 gap-2">
      <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#525252" }}>Véhicules du dossier</span>
      {dossierVehicles.map((v) => {
        const isSelected = v.id === selectedVehicleId;
        const imgUrl = getVehicleImageUrl(v.marque, v.modele, v.coloris);
        return (
          <button
            key={v.id}
            className="flex flex-row items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors"
            style={{
              borderColor: isSelected ? "#5C6BFF" : "#E5E5E5",
              background: isSelected ? "#F5F6FF" : "#FFFFFF",
            }}
            onClick={() => onSelect(v.id)}
          >
            <div
              className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
              style={{
                borderColor: isSelected ? "#5C6BFF" : "#D4D4D4",
                background: isSelected ? "#5C6BFF" : "transparent",
              }}
            >
              {isSelected && (
                <svg width={10} height={8} viewBox="0 0 10 8" fill="none">
                  <path d="M1 4l3 3 5-6" stroke="#FFFFFF" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
            {/* Vehicle thumbnail */}
            <div className="w-16 h-10 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              {imgUrl && !imageErrors[v.id] ? (
                <img
                  src={imgUrl}
                  alt={v.plate}
                  className="w-full h-full object-contain"
                  onError={() => setImageErrors((prev) => ({ ...prev, [v.id]: true }))}
                />
              ) : (
                <Icon1 name="image" size={16} color="#D4D4D4" />
              )}
            </div>
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-h3 font-medium truncate" style={{ fontFamily: "Poppins" }}>
                {v.marque} {v.modele}
              </span>
              <span className="text-h4 text-neutral-400" style={{ fontFamily: "Poppins" }}>
                {v.plate || "—"}{v.km ? ` · ${v.km.toLocaleString()} km` : ""}
              </span>
            </div>
            {isSelected && (
              <span className="text-h4 font-medium flex-shrink-0" style={{ color: "#5C6BFF" }}>Sélectionné</span>
            )}
          </button>
        );
      })}
      {dossierVehicles.length === 0 && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucun véhicule dans le dossier</span>
      )}
    </div>
  );
}

function DocumentsTab({ documents, onAdd }: { documents: Array<{ id?: string; name: string; type: string; created_at?: string }>; onAdd: () => void }) {
  return (
    <div className="flex flex-col p-4 gap-2">
      <div className="flex flex-row items-center justify-between">
        <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#525252" }}>Documents</span>
        <button
          onClick={onAdd}
          className="flex flex-row items-center gap-1 text-h4 font-medium hover:bg-neutral-100 rounded-lg px-2 py-1"
          style={{ color: "#6366f1" }}
        >
          <Icon1 name="plus" size={12} color="#6366f1" />
          Ajouter
        </button>
      </div>
      {documents.map((d, i) => (
        <div key={i} className="flex flex-row items-center gap-3 bg-white rounded-lg border border-neutral-200 px-3 py-2">
          <Icon1 name="file" size={16} color="#525252" />
          <div className="flex flex-col flex-1">
            <span className="text-h3 font-medium" style={{ fontFamily: "Poppins" }}>{d.name}</span>
            <span className="text-h4 text-neutral-400" style={{ fontFamily: "Poppins" }}>{d.type}</span>
          </div>
        </div>
      ))}
      {documents.length === 0 && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucun document</span>
      )}
    </div>
  );
}

const DOCUMENT_TYPE_OPTIONS = [
  { value: "carte_grise", label: "Carte grise" },
  { value: "assurance", label: "Assurance" },
  { value: "controle_technique", label: "Contrôle technique" },
  { value: "constat_amiable", label: "Constat amiable" },
  { value: "permis_conduire", label: "Permis de conduire" },
  { value: "devis", label: "Devis" },
  { value: "facture", label: "Facture" },
  { value: "bon_commande", label: "Bon de commande" },
  { value: "rapport_expertise", label: "Rapport d'expertise" },
  { value: "photo_vehicule", label: "Photo véhicule" },
  { value: "autre", label: "Autre" },
];

function AddDocumentModal({
  dossierId,
  onClose,
  onSuccess,
}: {
  dossierId: string;
  onClose: () => void;
  onSuccess: (dossier: Dossier) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [docName, setDocName] = useState("");
  const [docType, setDocType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setDocName(selectedFile.name);

    setClassifying(true);
    try {
      const result = await dossiersApi.classifyDocument(selectedFile);
      if (result.suggested_name) {
        const origExt = selectedFile.name.includes(".") ? selectedFile.name.substring(selectedFile.name.lastIndexOf(".")) : "";
        const suggestedHasExt = result.suggested_name.includes(".");
        setDocName(suggestedHasExt ? result.suggested_name : result.suggested_name + origExt);
      }
      if (result.type_key) setDocType(result.type_key);
    } catch {
      // Classification failed — user can still manually select type
    } finally {
      setClassifying(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const updated = await dossiersApi.uploadDocument(dossierId, file, docName || undefined, docType || undefined);
      showNotification({ type: "success", message: "Document ajouté" });
      onSuccess(updated);
      onClose();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'envoi" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <BaseModal
      title="Ajouter un document"
      startIcon={<Icon1 name="upload" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-4 p-4">
        {/* Drop zone */}
        <div
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${
            dragOver ? "border-indigo-400 bg-indigo-50" : "border-neutral-300 hover:bg-neutral-50"
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <Icon1 name="file" size={28} color="#6366f1" />
              <span className="text-h3 font-medium text-neutral-700 text-center">{file.name}</span>
              <span className="text-h4 text-neutral-400">{(file.size / 1024).toFixed(0)} Ko</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Icon1 name="upload" size={24} color="#A1A1A1" />
              <span className="text-h3 text-neutral-400">Glisser ou cliquer pour importer</span>
              <span className="text-h4 text-neutral-300">PDF, images, documents</span>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFileSelect(f);
            }}
          />
        </div>

        {/* AI classification */}
        {classifying && (
          <div className="flex flex-row items-center gap-2 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
            <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-h3 text-indigo-600">Analyse du document...</span>
          </div>
        )}

        <TextField label="Nom du document" value={docName} onChange={setDocName} />
        <DropDown
          label="Type de document"
          options={DOCUMENT_TYPE_OPTIONS}
          value={docType}
          onSelect={(v) => setDocType(String(v))}
        />
      </div>

      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button variant="small" label={uploading ? "Envoi..." : "Ajouter"} onClick={handleUpload} disabled={!file || uploading} />
      </div>
    </BaseModal>
  );
}

// ─── Main OrdreModal ───────────────────────────────────────

interface OrdreModalProps {
  ordre: Ordre;
  onClose: () => void;
  onSave: (ordre: Ordre) => void;
}

export default function OrdreModal({ ordre, onClose, onSave }: OrdreModalProps) {
  const [activeTab, setActiveTab] = useState("ordre");
  const [form, setForm] = useState<Ordre>({ ...ordre });
  const [subModal, setSubModal] = useState<string | null>(null);

  const update = <K extends keyof Ordre>(key: K, value: Ordre[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const [parentDossier, setParentDossier] = useState<Dossier | null>(null);
  useEffect(() => {
    if (ordre.dossier_id) {
      dossiersApi.get(ordre.dossier_id).then(setParentDossier).catch(() => {});
    }
  }, [ordre.dossier_id]);

  const orgData = useDataStore((s) => s.orgData);
  const fetchAtelierData = useAtelierStore((s) => s.fetchData);
  const atelierJobs = useAtelierStore((s) => s.jobs);
  const atelierWorkers = useAtelierStore((s) => s.workers);
  const atelierCharges = useAtelierStore((s) => s.charges);

  const [configuredInterventions, setConfiguredInterventions] = useState<Intervention[]>([]);

  useEffect(() => {
    if (orgData?.id) {
      fetchAtelierData(orgData.id);
      interventionsApi.list(orgData.id).then(setConfiguredInterventions).catch(() => {});
    }
  }, [orgData?.id]);

  const ordreAtelierCharges = useMemo(() => {
    const workerMap = new Map(atelierWorkers.map((w) => [w.id, w.name]));
    // Placed jobs (on the grid)
    const jobRows: AtelierChargeDisplay[] = atelierJobs
      .filter((j) => j.ordre_id === ordre.id)
      .map((j) => ({
        id: j.id,
        worker_name: workerMap.get(j.workerId) || "—",
        specialty: j.specialty,
        date: j.date,
        start_hour: j.startHour,
        duration_hours: j.durationHours,
      }));
    // Unplaced charges (still in sidebar)
    const chargeRows: AtelierChargeDisplay[] = atelierCharges
      .filter((c) => c.ordre_id === ordre.id)
      .map((c) => ({
        id: c.id,
        worker_name: "Non assigné",
        specialty: c.specialty,
        date: "—",
        start_hour: 0,
        duration_hours: c.hours,
      }));
    return [...jobRows, ...chargeRows];
  }, [atelierJobs, atelierWorkers, atelierCharges, ordre.id]);

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await ordresApi.update(ordre.id, {
        name: form.name,
        tags: form.tags,
        status: form.status,
        immatriculation: form.immatriculation,
        modele: form.modele,
        campagne: form.campagne,
        assigned_to: form.assigned_to,
        notes: form.notes,
        interventions: form.interventions,
        documents: form.documents,
        reception_date: form.reception_date,
        restitution: form.restitution,
      });
      useOrdreStore.getState().updateOrdre(updated);
      // Refetch planning appointments so the grid reflects updated times
      const planningState = usePlanningStore.getState();
      if (planningState._fetchedOrgId) {
        planningState.fetchAppointments(planningState._fetchedOrgId);
      }
      onSave(updated);
      showNotification({ type: "success", message: "Ordre enregistré" });
      onClose();
    } catch (e) {
      console.error("Error saving ordre:", e);
      showNotification({ type: "error", message: "Erreur lors de l'enregistrement" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <BaseModal
        title={`Modifier RDV — ${form.num}`}
        startIcon={<Icon1 name="planning" size={20} />}
        onClose={onClose}
        width="max-w-4xl"
      >
        {/* Tabs */}
        <div className="px-4 pt-3 overflow-hidden">
          <ChunkyTabs
            activeTab={activeTab}
            tabs={TABS}
            setActiveTab={setActiveTab}
          />
        </div>

        {/* Tab content */}
        <div className="min-h-300 overflow-x-hidden">
          {activeTab === "ordre" && <OrdreTab form={form} update={update} />}
          {activeTab === "interventions" && (
            <InterventionsTab
              interventions={form.interventions}
              onAdd={() => setSubModal("intervention")}
              configuredInterventions={configuredInterventions}
              onAddFromCatalog={(catalog) => {
                const hours = +(catalog.estimated_minutes / 60).toFixed(2);
                update("interventions", [...form.interventions, {
                  id: `i-${Date.now()}`,
                  label: catalog.name,
                  default_hours: hours,
                  estimated_hours: hours,
                  assigned_to: "",
                }]);
              }}
            />
          )}
          {activeTab === "atelier" && <AtelierTab charges={ordreAtelierCharges} />}
          {activeTab === "contacts" && (
            <ContactsTab
              contacts={form.contacts || []}
              onAdd={() => setSubModal("contact")}
            />
          )}
          {activeTab === "vehicules" && (
            <VehiculesTab
              dossierVehicles={parentDossier?.vehicles || (form.vehicle ? [form.vehicle] : [])}
              selectedVehicleId={form.vehicle_id || form.vehicle?.id}
              onSelect={(vehicleId) => {
                const v = (parentDossier?.vehicles || []).find((veh) => veh.id === vehicleId);
                update("vehicle_id", vehicleId);
                if (v) update("vehicle", v);
              }}
            />
          )}
          {activeTab === "documents" && (
            <DocumentsTab
              documents={parentDossier?.documents || form.documents}
              onAdd={() => setSubModal("document")}
            />
          )}
          {activeTab === "communications" && (
            <div style={{ height: 500 }}>
              <ChatPanel
                discussionId={parentDossier?.discussion_id || ordre.discussion_id}
                internalDiscussionId={parentDossier?.internal_discussion_id || ordre.internal_discussion_id}
                entityType="ordre"
                entityId={ordre.id}
                entityName={ordre.name}
                compact
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
          <SecondaryButton variant="small" onClick={onClose}>
            Fermer
          </SecondaryButton>
          <Button variant="small" label={saving ? "Enregistrement..." : "Enregistrer"} onClick={handleSave} disabled={saving} />
        </div>
      </BaseModal>

      {/* Sub-modals */}
      {subModal === "intervention" && <AddInterventionModal onClose={() => setSubModal(null)} />}
      {subModal === "contact" && <AddContactModal onClose={() => setSubModal(null)} />}
      {subModal === "vehicle" && <AddVehicleModal onClose={() => setSubModal(null)} />}
      {subModal === "document" && ordre.dossier_id && (
        <AddDocumentModal
          dossierId={ordre.dossier_id}
          onClose={() => setSubModal(null)}
          onSuccess={(updatedDossier) => setParentDossier(updatedDossier)}
        />
      )}
    </>
  );
}
